import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type VideoJs from "video.js";
import { AnimatePresence, motion } from "motion/react";
import { CONFIG } from "./config";
import { loadPlayer, loadCastSdkIfUsable, lockLandscapeForFullscreen } from "./player-core";
import {
  isAndroidWebView,
  isChromeLike,
  showEscape,
  isInAppShell,
  STANDALONE_PLAYER_URL,
  getIdentityForHandoff,
} from "./identity";

// Cast SDK is only fetched where it can work (real Chrome), so it never
// costs anything on the app's WebView.
loadCastSdkIfUsable(isChromeLike, isAndroidWebView);

type Status = "connecting" | "live" | "waiting" | "ended" | "blocked" | "unauthorized";

export default function Player() {
  // The player lives in a detached host div and is physically relocated
  // into the modal when opened — same approach as RecordingsView. It must
  // keep existing while the modal is closed, or the live poll that detects
  // the class starting would stop running.
  const playerHostRef = useRef<HTMLDivElement | null>(null);
  const modalSlotRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLElement | null>(null);
  const playerRef = useRef<ReturnType<typeof VideoJs> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<Status>("connecting");
  // Pre-minted so the escape link's href is already correct before the user
  // taps it. Falls back to the standalone player (a foreign domain, so the
  // app still hands off navigation to it) if the worker call fails or
  // hasn't resolved yet.
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);
  // The Cloudflare Stream video now requires a signed token — the plain
  // public video id no longer plays at all — so this must resolve before
  // any playback attempt. On the standalone page reached via a handoff
  // link, ?t= in our own URL carries the already-established identity;
  // otherwise (embedded on the Kajabi page) mint fresh from local identity.
  const [streamToken, setStreamToken] = useState<string | null | undefined>(undefined);
  // Lets the "no class running" overlay's Refresh button force an immediate
  // re-check instead of waiting out the rest of the current poll interval —
  // assigned inside the player effect below since attemptLoad() only exists
  // in that closure.
  const retryRef = useRef<() => void>(() => {});
  const [isOpen, setIsOpen] = useState(false);
  // Standalone (?t=) means we arrived from "Watch in Fullscreen". Browsers
  // refuse autoplay AND fullscreen without a real gesture, so — exactly as
  // the recordings hand-off does — one tap here does play + fullscreen +
  // landscape together, which is a valid gesture and actually works.
  const arrivedFromHandoff = !!new URLSearchParams(window.location.search).get("t");
  const [handoffStarted, setHandoffStarted] = useState(false);
  // Inside the Kajabi app the page never scrolls (the native app scrolls
  // and sizes the WebView to the FULL content height), so position:fixed
  // centres on the whole document — which is why the modal landed far down
  // the page over the schedule text. When the page can't scroll itself we
  // anchor to the card instead, which is on screen by definition.
  const [anchorTop, setAnchorTop] = useState<number | null>(null);
  const pushedHistoryRef = useRef(false);

  useEffect(() => {
    if (!CONFIG.handoffWorkerUrl) {
      setStreamToken(null);
      return;
    }

    const tFromUrl = new URLSearchParams(window.location.search).get("t");

    const request = tFromUrl
      ? fetch(`${CONFIG.handoffWorkerUrl}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ t: tFromUrl }),
        }).then((r) => (r.ok ? r.json() : null))
      : fetch(`${CONFIG.handoffWorkerUrl}/mint`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(getIdentityForHandoff()),
        }).then((r) => (r.ok ? r.json() : null));

    request
      .then((data) => {
        if (data?.url) setHandoffUrl(data.url);
        setStreamToken(data?.streamToken ?? null);
      })
      .catch(() => setStreamToken(null));
  }, []);

  useEffect(() => {
    if (!streamToken) return;

    const manifest = `https://${CONFIG.customerSubdomain}/${streamToken}/manifest/video.m3u8`;

    // video.js now arrives in its own chunk, so setup waits for it.
    // `cancelled` covers the effect being torn down before it lands.
    let cancelled = false;
    loadPlayer().then((videojs) => {
      if (!cancelled) setup(videojs);
    });

    function setup(videojs: typeof VideoJs) {
    if (!playerHostRef.current) playerHostRef.current = document.createElement("div");
    const videoEl = document.createElement("video");
    videoEl.className = "video-js vjs-fluid vjs-big-play-centered";
    videoEl.setAttribute("playsinline", "");
    videoEl.setAttribute("x-webkit-airplay", "allow");
    videoEl.setAttribute("preload", "auto");
    videoEl.setAttribute("crossorigin", "anonymous");
    playerHostRef.current.appendChild(videoEl);

    const player = videojs(videoEl, {
      controls: true,
      fluid: true,
      // Confirmed live: video.js's default inactivity auto-hide makes the
      // underlying <video> intercept the FIRST tap on any control (play,
      // fullscreen) once the bar has faded — the tap "wakes up" the UI
      // instead of reaching the button, so it looks like the button just
      // doesn't work until a second tap. Controls stay visible/clickable
      // always instead.
      inactivityTimeout: 0,
      liveui: CONFIG.isLive,
      playsinline: true,
      poster: CONFIG.poster || undefined,
      techOrder: ["chromecast", "html5"],
      // Fullscreen and PiP are blocked at the Android/iOS WebView level
      // inside Kajabi's branded app (the app's WebViewClient doesn't wire
      // up the native support HTML5 fullscreen needs) — tapping either
      // button there does nothing, so they're dead UI exactly where
      // showEscape is true. Real fullscreen/PiP still works fine in a
      // normal browser tab, which is what the escape button sends you to.
      controlBar: showEscape ? { fullscreenToggle: false, pictureInPictureToggle: false } : undefined,
      plugins: {
        chromecast: {},
        airPlay: {},
      },
      html5: {
        vhs: {
          overrideNative: false,
          limitRenditionByPlayerDimensions: true,
        },
        nativeAudioTracks: true,
        nativeVideoTracks: true,
      },
    });
    playerRef.current = player;

    function attemptLoad() {
      // Cache-bust every attempt — otherwise the browser/CDN can keep
      // serving a stale "not live yet" response for this exact URL and the
      // poll never notices the instructor actually went live.
      const src = manifest + (manifest.includes("?") ? "&" : "?") + "_=" + Date.now();
      player.src({ src, type: "application/x-mpegURL" });
      player.load();
    }

    function schedulePoll() {
      if (pollTimer.current) clearTimeout(pollTimer.current);
      pollTimer.current = setTimeout(() => {
        attemptLoad();
      }, CONFIG.pollIntervalMs);
    }

    // Live HLS often rejects an early play() because the live edge hasn't
    // buffered enough yet — the browser just silently refuses, and nothing
    // retries on its own, so a user has to keep re-tapping play until enough
    // buffer happens to accumulate by chance. Retry automatically instead.
    let playRetries = 0;
    function tryPlay() {
      const attempt = player.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => {
          if (playRetries++ < 5) setTimeout(tryPlay, 700);
        });
      }
    }

    // Tracked locally (not via the `status` state) since this effect's
    // event handlers close over values from when it first ran — reading
    // React state here would always see the initial "connecting" value.
    let isLiveNow = false;

    player.on("loadedmetadata", () => {
      setStatus("live");
      isLiveNow = true;
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
      playRetries = 0;
      tryPlay();
    });

    // A play() that gets silently rejected often shows up as a `pause`
    // firing right after `play`, rather than a promise rejection video.js
    // surfaces anywhere — catch that pattern too and retry the same way.
    let lastPlayAt = 0;
    player.on("play", () => { lastPlayAt = Date.now(); });
    player.on("pause", () => {
      if (isLiveNow && Date.now() - lastPlayAt < 500 && playRetries < 5) {
        playRetries++;
        setTimeout(tryPlay, 700);
      }
    });

    player.on("error", () => {
      const err = player.error();
      const code = err ? err.code : 0;

      if (CONFIG.isLive && (code === 2 || code === 4)) {
        // No broadcast currently reaching Cloudflare — keep checking back
        // instead of giving up, so the player picks the class up on its own.
        setStatus("waiting");
        isLiveNow = false;
        schedulePoll();
      } else if (showEscape) {
        setStatus("blocked");
      } else {
        setStatus("ended");
      }
    });

    // A clean stream stop (instructor ends the broadcast) surfaces as
    // `ended`, not `error` — HLS marks the playlist with an end tag rather
    // than failing. Without this, the player just sits there forever after
    // a stop, with nothing left to notice if the class restarts.
    if (CONFIG.isLive) {
      player.on("ended", () => {
        setStatus("waiting");
        isLiveNow = false;
        schedulePoll();
      });
    }

    retryRef.current = () => {
      setStatus("connecting");
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
      attemptLoad();
    };

    attemptLoad();
    }

    return () => {
      cancelled = true;
      if (pollTimer.current) clearTimeout(pollTimer.current);
      playerRef.current?.dispose();
      playerRef.current = null;
    };
  }, [streamToken]);

  useEffect(() => {
    // streamToken resolved to null (not undefined) — the worker couldn't
    // confirm entitlement, so no signed playback token exists. Nothing to
    // retry here; the video literally cannot load without one now that
    // Stream requires signed URLs.
    if (streamToken === null) setStatus("unauthorized");
  }, [streamToken]);

  // Move the live player into the modal when it opens, and back out to
  // its detached host when it closes — the player instance itself is never
  // torn down, so the live poll keeps running either way.
  // Relocate the (detached, always-running) player into the modal when
  // open. It keeps existing while closed so the live poll keeps running.
  useEffect(() => {
    const host = playerHostRef.current;
    const slot = modalSlotRef.current;
    if (isOpen && host && slot && host.parentElement !== slot) slot.appendChild(host);
  }, [isOpen, streamToken, status]);

  function startHandoffPlayback() {
    setHandoffStarted(true);
    const player = playerRef.current;
    if (!player) return;
    player.play()?.catch(() => {});
    try {
      const el = playerHostRef.current?.querySelector(".video-js") as
        | (HTMLElement & { requestFullscreen?: () => Promise<void> })
        | null;
      el?.requestFullscreen?.()
        .then(() => lockLandscapeForFullscreen())
        .catch(() => {});
    } catch {
      // Fullscreen unavailable here — playback still starts.
    }
  }

  function openModal() {
    // See RecordingsView: fixed positioning is unreliable inside the app
    // webview, so anchor to the card there instead.
    if (isInAppShell() && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setAnchorTop(Math.max(0, rect.top + (window.scrollY || 0)));
    } else {
      setAnchorTop(null);
    }
    setIsOpen(true);
    if (!pushedHistoryRef.current) {
      try {
        history.pushState({ cfpModal: true }, "");
        pushedHistoryRef.current = true;
      } catch {
        // history unavailable — back button just won't close it
      }
    }
  }

  function closeModal() {
    playerRef.current?.pause();
    setIsOpen(false);
    if (pushedHistoryRef.current) {
      pushedHistoryRef.current = false;
      try {
        history.back();
      } catch {
        // nothing to unwind
      }
    }
  }

  // Arriving from a hand-off opens straight into the modal, same as the
  // recordings destination — not a different-looking page.
  useEffect(() => {
    if (arrivedFromHandoff && streamToken && !isOpen && !handoffStarted) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedFromHandoff, streamToken]);

  useEffect(() => {
    function onPopState() {
      if (pushedHistoryRef.current) {
        pushedHistoryRef.current = false;
        playerRef.current?.pause();
        setIsOpen(false);
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // "connecting" also gets a covering overlay now — otherwise video.js's own
  // play button sits there fully visible and tappable before the first
  // manifest check has resolved, so taps land on nothing and it feels
  // broken even though it's about to work on its own.
  const overlayText: Record<Exclude<Status, "live">, [string, string]> = {
    connecting: ["Connecting…", "Getting the class ready."],
    waiting: [
      "No class running right now",
      "The session appears here automatically when your instructor goes live.",
    ],
    blocked: [
      "This class won't play here",
      `Tap 'Watch in Fullscreen' below — it plays reliably outside the app.`,
    ],
    ended: ["This class won't play right now", "Try refreshing the page."],
    unauthorized: [
      "Sign in required",
      "You'll need an active subscription to this course to watch — open this link from inside your course, not directly.",
    ],
  };

  const showOverlay = status !== "live";
  const [overlayHead, overlayBody] = showOverlay
    ? overlayText[status as Exclude<Status, "live">]
    : ["", ""];

  // The card invites a tap; the modal must not ("tap to watch" makes no
  // sense once you're already inside it, with nothing to tap).
  const cardLabel =
    status === "live"
      ? "Live now — tap to watch"
      : status === "waiting"
        ? "No class running right now"
        : status === "connecting"
          ? "Connecting…"
          : status === "unauthorized"
            ? "Sign in required"
            : "Tap to watch";

  const modalLabel =
    status === "live"
      ? "Streaming now"
      : status === "waiting"
        ? "Hasn't started yet"
        : status === "connecting"
          ? "Connecting…"
          : status === "unauthorized"
            ? "Sign in required"
            : "";

  return (
    <div id="cf-class-player">
      <motion.button
        type="button"
        className="cfp-card cfp-live-card"
        ref={(el) => { cardRef.current = el as HTMLElement | null; }}
        layoutId="live-class"
        onClick={openModal}
        aria-label={`Open ${CONFIG.title}`}
      >
        <motion.div className="cfp-card-media cfp-live-card-media" layoutId="live-class-media">
          {CONFIG.poster ? (
            <img className="cfp-card-thumb" alt="" src={CONFIG.poster} />
          ) : (
            <div className="cfp-live-card-art">
              <span className="cfp-card-play" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="30" height="30">
                  <path fill="currentColor" d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
          )}
          {status === "live" && (
            <span className="cfp-live-badge">
              <i /> LIVE
            </span>
          )}
        </motion.div>
        <div className="cfp-card-info">
          <p className="cfp-card-title">{CONFIG.title}</p>
          <p className="cfp-card-meta">{cardLabel}</p>
        </div>
      </motion.button>

      {createPortal(
        <>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="cfp-modal-backdrop"
              style={anchorTop !== null ? { position: "absolute", top: 0, left: 0, right: 0, height: "100%", alignItems: "flex-start" } : undefined}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            >
              <motion.div
                className="cfp-modal"
                style={anchorTop !== null ? { marginTop: anchorTop } : undefined}
                layoutId="live-class"
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div className="cfp-modal-media" layoutId="live-class-media">
                  <div className="cfp-row-slot" ref={modalSlotRef} />
                {arrivedFromHandoff && !handoffStarted && status !== "unauthorized" && (
                  <button type="button" className="cfp-solo-start" onClick={startHandoffPlayback}>
                    <span className="cfp-solo-start-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="34" height="34">
                        <path fill="currentColor" d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                    <span className="cfp-solo-start-label">Tap to play fullscreen</span>
                  </button>
                )}
                  <div className="cfp-overlay" hidden={!showOverlay}>
                    <p className="cfp-overlay-head">{overlayHead}</p>
                    <p className="cfp-overlay-body">{overlayBody}</p>
                    {(status === "connecting" || status === "waiting" || status === "ended") && (
                      <button type="button" className="cfp-overlay-refresh" onClick={() => retryRef.current()}>
                        Refresh
                      </button>
                    )}
                  </div>
                </motion.div>

                <div className="cfp-modal-bar">
                  <div className="cfp-modal-meta">
                    <p className="cfp-modal-title cfp-modal-title-row">
                      {status === "live" && (
                        <span className="cfp-live cfp-live-inline">
                          <i /> Live
                        </span>
                      )}
                      <span className="cfp-modal-title-text">{CONFIG.title}</span>
                    </p>
                    {modalLabel && <p className="cfp-modal-sub">{modalLabel}</p>}
                  </div>

                  {showEscape && (
                    <a
                      className="cfp-action cfp-action-fs"
                      href={handoffUrl ?? STANDALONE_PLAYER_URL}
                      rel="noopener noreferrer"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                      </svg>
                      <span>Watch in Fullscreen</span>
                    </a>
                  )}
                </div>

                <button type="button" className="cfp-modal-close" onClick={closeModal} aria-label="Close">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </>,
        document.body
      )}

      <div className="cfp-info">
        <p className="cfp-info-name">
          <strong>CrossFit Gymnastics LIVE</strong>
          <br />
          Trainer: Ervin Kalemi
        </p>
        <p className="cfp-info-dates">
          <strong>Program starts: September 6, 2026</strong>
          <br />
          <strong>First live class: Tuesday, September 8, 2026</strong>
        </p>
        <p className="cfp-info-schedule">
          Tuesday &amp; Thursday
          <br />
          19:00&ndash;20:00
          <br />
          2 live classes per week
          <br />
          Duration: 60 minutes
        </p>
      </div>

      <p className="cfp-help">
        Trouble playing or casting?{" "}
        <a href={handoffUrl ?? STANDALONE_PLAYER_URL} rel="noopener noreferrer">
          Open this class in your browser
        </a>
      </p>
    </div>
  );
}
